import { toBool } from './dbOverviewSections';

/**
 * The ER diagram's data: tables with their columns, and foreign keys paired
 * column to column. Pure — built from two catalog result sets, so it can be
 * tested (and reasoned about) without a database or React Flow.
 */

/** Diagram cap: above this many tables a full drawing stops being readable. */
export const MAX_DIAGRAM_TABLES = 120;

export const tableKey = (schema, name) => `${schema || ''}.${name}`;

/**
 * @param columnRows [{ schema, table, column, type, pk, nullable }] in column order
 * @param fkRows     [{ schema, table, constraint, column, refSchema, refTable, refColumn }]
 *                   ordered by constraint, then position — the order pairs them
 */
export function buildDiagramModel(columnRows, fkRows) {
  const tables = new Map();
  for (const r of columnRows || []) {
    if (!r || r.table == null) continue;
    const key = tableKey(r.schema, r.table);
    let t = tables.get(key);
    if (!t) {
      t = { key, schema: r.schema || '', name: String(r.table), columns: [] };
      tables.set(key, t);
    }
    if (r.column != null) {
      t.columns.push({
        name: String(r.column),
        type: r.type == null ? '' : String(r.type),
        pk: toBool(r.pk),
        nullable: toBool(r.nullable),
        fk: false,
        ref: false,
      });
    }
  }

  // Group FK rows into constraints: columns[i] → refColumns[i].
  const byConstraint = new Map();
  for (const r of fkRows || []) {
    if (!r || r.table == null || r.refTable == null || r.column == null) continue;
    const from = tableKey(r.schema, r.table);
    const id = `${from}\u0000${r.constraint}`;
    let fk = byConstraint.get(id);
    if (!fk) {
      fk = {
        id: `fk:${from}:${r.constraint}`,
        name: r.constraint == null ? '' : String(r.constraint),
        from,
        to: tableKey(r.refSchema || r.schema, r.refTable),
        columns: [],
        refColumns: [],
      };
      byConstraint.set(id, fk);
    }
    fk.columns.push(String(r.column));
    fk.refColumns.push(r.refColumn == null ? null : String(r.refColumn));
  }

  const fks = [];
  for (const fk of byConstraint.values()) {
    // SQLite leaves `to` NULL when the key references the implicit primary
    // key: the i-th column of the constraint means the i-th PK column.
    if (fk.refColumns.some((c) => c == null)) {
      const target = tables.get(fk.to);
      const pks = target ? target.columns.filter((c) => c.pk).map((c) => c.name) : [];
      fk.refColumns = fk.refColumns.map((c, i) => c ?? pks[i] ?? null);
    }
    if (fk.refColumns.some((c) => c == null)) continue;
    const source = tables.get(fk.from);
    if (source) {
      for (const col of source.columns) if (fk.columns.includes(col.name)) col.fk = true;
    }
    const target = tables.get(fk.to);
    if (target) {
      for (const col of target.columns) if (fk.refColumns.includes(col.name)) col.ref = true;
    }
    fks.push(fk);
  }
  return { tables, fks };
}

/** Table keys connected to `key` within `depth` foreign-key hops (either direction). */
export function relatedKeys(model, key, depth = 1) {
  const seen = new Set([key]);
  let frontier = [key];
  for (let d = 0; d < depth; d += 1) {
    const next = [];
    for (const fk of model.fks) {
      for (const k of frontier) {
        if (fk.from === k && !seen.has(fk.to)) {
          seen.add(fk.to);
          next.push(fk.to);
        }
        if (fk.to === k && !seen.has(fk.from)) {
          seen.add(fk.from);
          next.push(fk.from);
        }
      }
    }
    frontier = next;
    if (!frontier.length) break;
  }
  return seen;
}

/**
 * Which tables to draw.
 *
 *  - `schema`: Postgres over every schema draws one schema at a time.
 *  - `focus`:  only that table and its neighbours (DataGrip's "only related").
 *  - otherwise every table, up to `cap` — the most connected first, since a
 *    cut diagram should keep the relations rather than the alphabet.
 *
 * Returns `{ keys, total, capped }`; `total` counts the candidates before the
 * cap so the notice can say "120 of 900".
 */
export function selectDiagramTables(model, { schema = null, focus = null, depth = 1, cap = MAX_DIAGRAM_TABLES } = {}) {
  let candidates = [...model.tables.values()].filter((t) => schema == null || t.schema === schema);
  if (focus && model.tables.has(focus)) {
    const keep = relatedKeys(model, focus, depth);
    candidates = candidates.filter((t) => keep.has(t.key));
    // The focus table itself stays even if it is in another schema.
    if (!candidates.some((t) => t.key === focus)) candidates.push(model.tables.get(focus));
  }
  const total = candidates.length;
  if (total <= cap) return { keys: new Set(candidates.map((t) => t.key)), total, capped: false };
  const degree = new Map();
  for (const fk of model.fks) {
    degree.set(fk.from, (degree.get(fk.from) || 0) + 1);
    degree.set(fk.to, (degree.get(fk.to) || 0) + 1);
  }
  const ranked = [...candidates].sort(
    (a, b) => (degree.get(b.key) || 0) - (degree.get(a.key) || 0) || a.key.localeCompare(b.key)
  );
  return { keys: new Set(ranked.slice(0, cap).map((t) => t.key)), total, capped: true };
}

/** The foreign keys whose both ends are drawn. */
export function visibleForeignKeys(model, keys) {
  return model.fks.filter((fk) => keys.has(fk.from) && keys.has(fk.to));
}

/** Schemas present in the model, sorted, for the schema picker. */
export function diagramSchemas(model) {
  return [...new Set([...model.tables.values()].map((t) => t.schema))].sort();
}
